import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Link, LinearProgress, Paper, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import { DEEP_GREEN, MUTED } from '../colorTokens';
import { VERDICT_COLOR, VERDICT_LABEL, formatDate, formatNumber, formatPercent } from './format';
import { startHealthSync, useHealth, useMicroservices } from './useReleaseCadence';
import { Headline, NUMERALS, ScoreCard } from './ScoreParts';
import { NoRolloutNotice, VersionPicker, useVersionPicker } from './VersionPicker';

const POLL_MS = 8000;
const GROUP_ORDER = ['internal', 'preprod', 'standard', 'vip'];
const GROUP_LABEL = { internal: 'Internal', preprod: 'Pre-prod', standard: 'Standard', vip: 'VIP' };
const BAND_LABEL = { high: 'Healthy', medium: 'Some concerns', low: 'Poor health' };
const SEVERITY_LABEL = { critical: 'Critical', high: 'High', medium: 'Medium', low: 'Low' };
const HEAD_SX = { bgcolor: '#F8F9FA', '& th': { color: DEEP_GREEN, fontWeight: 600, borderBottom: '2px solid rgba(26,46,34,0.15)' } };
const CARD_SX = { borderRadius: '12px', borderColor: 'rgba(0,0,0,0.05)' };

const formatRatio = (r) => (r == null ? '–' : `${r.toFixed(2)}x`);
const formatShare = (s) => (s == null ? '–' : formatPercent(s, 2));

const VerdictChip = ({ verdict }) => (
  <Chip size="small" label={VERDICT_LABEL[verdict]} sx={{ height: 24, borderRadius: '8px', fontWeight: 600, color: '#fff', bgcolor: VERDICT_COLOR[verdict] }} />
);

const SyncLine = ({ sync, onSync, starting, error }) => {
  const syncing = starting || (sync && sync.syncing);
  const last = sync && sync.lastSync;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
      <Button size="small" variant="outlined" onClick={onSync} disabled={syncing} startIcon={syncing ? <CircularProgress size={14} /> : <SyncIcon />} sx={{ textTransform: 'none', borderRadius: '10px' }}>
        {syncing ? 'Reading Datadog and Azure DevOps…' : 'Refresh health data'}
      </Button>
      {error && <Typography variant="body2" color="error">{error}</Typography>}
      {!error && last && last.problems && last.problems.length > 0 && (
        <Typography variant="body2" color="error">Last refresh had problems: {last.problems.join('; ')}</Typography>
      )}
      {!error && last && (!last.problems || !last.problems.length) && (
        <Typography variant="caption" sx={{ color: MUTED }}>
          Last refresh {formatDate(last.at)} (all versions): {formatNumber(last.errors ? last.errors.measured : 0)} waves re-measured, {formatNumber(last.bugs ? last.bugs.kept : 0)} bugs read from Azure DevOps. Refreshes automatically every night.
        </Typography>
      )}
    </Box>
  );
};

const Summary = ({ data }) => {
  const { score, verdict, checks, provisional, rollout, waveCoverage } = data;
  return (
    <ScoreCard headline={<Headline caption={`Post-release health score · ${data.version}`} value={String(score.value)} label={`out of 100 · ${BAND_LABEL[score.band]}${provisional ? ' (provisional)' : ''}`} />}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 6 }}>
        <Box>
          <Typography variant="caption" sx={{ color: MUTED }}>Health verdict</Typography>
          <Box sx={{ mt: 0.5 }}><VerdictChip verdict={verdict} /></Box>
          <Box sx={{ mt: 0.75 }}>
            {checks.map((c) => (
              <Tooltip key={c.key} title={c.detail} placement="right">
                <Typography variant="body2" sx={{ color: MUTED }}>
                  <Box component="span" sx={{ color: VERDICT_COLOR[c.status], fontWeight: 700 }}>{VERDICT_LABEL[c.status]}</Box>
                  {' · '}{c.label}
                </Typography>
              </Tooltip>
            ))}
          </Box>
        </Box>
        <Box>
          <Typography variant="caption" sx={{ color: MUTED }}>Rollout window</Typography>
          <Typography sx={{ fontWeight: 600, color: DEEP_GREEN }}>{formatDate(rollout.start)} to {formatDate(rollout.end)}</Typography>
          <Typography variant="body2" sx={{ color: provisional ? VERDICT_COLOR.caution : MUTED, fontWeight: provisional ? 600 : 400 }}>{provisional ? (data.observing || data.finalWavePending ? 'Provisional: the release is not fully observed yet' : 'Provisional: some data has not been refreshed recently') : 'Rollout finished and observed'}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" sx={{ color: MUTED }}>Waves graded for errors</Typography>
          <Typography sx={{ fontWeight: 600, color: DEEP_GREEN }}>{waveCoverage.graded} of {waveCoverage.total}</Typography>
          <Typography variant="body2" sx={{ color: MUTED }}>Waves without a clean before and after window are not graded</Typography>
        </Box>
      </Box>
    </ScoreCard>
  );
};

const displayValue = (c) => {
  if (c.value == null) return '–';
  if (c.key === 'errorChange') return formatRatio(c.value);
  if (c.key === 'escapedBugs') return `${formatNumber(c.value)} pts`;
  return formatNumber(c.value);
};

const ScoreBreakdown = ({ score }) => (
  <Paper variant="outlined" sx={{ ...CARD_SX, overflowX: 'auto' }}>
    <Typography sx={{ fontWeight: 600, color: DEEP_GREEN, p: 2, pb: 1 }}>How the score is built</Typography>
    <Table size="small">
      <TableHead>
        <TableRow sx={HEAD_SX}>
          <TableCell>Component</TableCell>
          <TableCell align="right">Weight</TableCell>
          <TableCell align="right">Value</TableCell>
          <TableCell sx={{ width: 220 }}>Points earned</TableCell>
          <TableCell>Basis</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {score.components.map((c) => (
          <TableRow key={c.key} sx={{ opacity: c.available ? 1 : 0.55 }}>
            <TableCell sx={{ fontWeight: 600 }}>{c.label}</TableCell>
            <TableCell align="right">{c.available ? c.weightShare : '–'}</TableCell>
            <TableCell align="right" sx={NUMERALS}>{displayValue(c)}</TableCell>
            <TableCell>
              {c.available ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LinearProgress variant="determinate" value={c.fraction * 100} sx={{ flex: 1, height: 8, borderRadius: 4, bgcolor: 'rgba(0,0,0,0.06)', '& .MuiLinearProgress-bar': { bgcolor: DEEP_GREEN } }} />
                  <Typography variant="caption" sx={{ minWidth: 70, textAlign: 'right' }}>{c.points} of {c.weightShare}</Typography>
                </Box>
              ) : (
                <Typography variant="caption" sx={{ color: MUTED }}>Left out</Typography>
              )}
            </TableCell>
            <TableCell sx={{ color: MUTED }}>{c.detail}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </Paper>
);

const WaveErrors = ({ waves }) => {
  const sorted = useMemo(() => [...waves].sort((a, b) => GROUP_ORDER.indexOf(a.group) - GROUP_ORDER.indexOf(b.group)), [waves]);
  return (
    <Paper variant="outlined" sx={{ ...CARD_SX, overflowX: 'auto' }}>
      <Typography sx={{ fontWeight: 600, color: DEEP_GREEN, p: 2, pb: 0.5 }}>Error rate around each wave</Typography>
      <Typography variant="body2" sx={{ color: MUTED, px: 2, pb: 1 }}>
        Errors as a share of all Collaborate logs in the regions the wave touched, 48 hours before the wave against up to 48 hours after it. Logs carry no version, so this reads the change around the rollout rather than the version itself.
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow sx={HEAD_SX}>
            <TableCell>Wave</TableCell>
            <TableCell align="right">Instances</TableCell>
            <TableCell align="right">Error share before</TableCell>
            <TableCell align="right">Error share after</TableCell>
            <TableCell align="right">Change</TableCell>
            <TableCell>Result</TableCell>
            <TableCell>Note</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map((w) => (
            <TableRow key={w.group} hover>
              <TableCell sx={{ fontWeight: 600 }}>
                <Tooltip title={`Regions: ${w.regions.join(', ')}`} placement="right"><span>{GROUP_LABEL[w.group] || w.group}</span></Tooltip>
              </TableCell>
              <TableCell align="right">{formatNumber(w.instances)}</TableCell>
              <TableCell align="right" sx={NUMERALS}>{formatShare(w.before.share)}</TableCell>
              <TableCell align="right" sx={NUMERALS}>{formatShare(w.after.share)}</TableCell>
              <TableCell align="right" sx={{ ...NUMERALS, fontWeight: 700, color: VERDICT_COLOR[w.status] }}>{formatRatio(w.ratio)}</TableCell>
              <TableCell><VerdictChip verdict={w.status} /></TableCell>
              <TableCell sx={{ color: MUTED }}>{w.note}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
};

const SyncedLine = ({ asOf, stale }) =>
  asOf ? (
    <Typography variant="caption" sx={{ display: 'block', px: 2, pb: 1, color: stale ? VERDICT_COLOR.caution : MUTED, fontWeight: stale ? 600 : 400 }}>
      {stale ? 'Out of date: last synced' : 'Last synced'} {formatDate(asOf)}
    </Typography>
  ) : null;

const BugsCard = ({ bugs, asOf, stale }) => {
  const escaped = bugs.list.filter((b) => b.source === 'Production' || b.source === 'Monitoring');
  return (
    <Paper variant="outlined" sx={{ ...CARD_SX, overflowX: 'auto' }}>
      <Typography sx={{ fontWeight: 600, color: DEEP_GREEN, p: 2, pb: 0.5 }}>Bugs raised against this version</Typography>
      <SyncedLine asOf={asOf} stale={stale} />
      <Typography variant="body2" sx={{ color: MUTED, px: 2, pb: 1 }}>
        {formatNumber(bugs.total)} bugs list this version under Affects Version: {formatNumber(bugs.foundInTesting)} found in testing and {formatNumber(bugs.escaped)} escaped to production ({formatNumber(bugs.escapedOpen)} still open).
        {' '}Escaped bugs score critical {bugs.bySeverity.critical}, high {bugs.bySeverity.high}, medium {bugs.bySeverity.medium}, low {bugs.bySeverity.low}, which is {bugs.points} points.
      </Typography>
      {escaped.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow sx={HEAD_SX}>
              <TableCell>Bug</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Severity</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>State</TableCell>
              <TableCell>Raised</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {escaped.map((b) => (
              <TableRow key={b.id} hover>
                <TableCell><Link href={b.url} target="_blank" rel="noopener noreferrer" underline="hover">{b.id}</Link></TableCell>
                <TableCell>{b.title}</TableCell>
                <TableCell>{SEVERITY_LABEL[b.severity] || b.severity}</TableCell>
                <TableCell>{b.source}</TableCell>
                <TableCell>{b.state}</TableCell>
                <TableCell>{formatDate(b.createdDate)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Paper>
  );
};

const IncidentsCard = ({ incidents, asOf, stale }) => (
  <Paper variant="outlined" sx={{ ...CARD_SX, overflowX: 'auto' }}>
    <Typography sx={{ fontWeight: 600, color: DEEP_GREEN, p: 2, pb: 0.5 }}>Production incidents</Typography>
    <SyncedLine asOf={asOf} stale={stale} />
    <Typography variant="body2" sx={{ color: MUTED, px: 2, pb: incidents.list.length ? 1 : 2 }}>
      {incidents.total
        ? `${formatNumber(incidents.total)} P1 or P2 incidents opened between ${formatDate(incidents.from)} and ${formatDate(incidents.to)} (rollout start to a week after it ended), ${formatNumber(incidents.open)} still open. Attribution is by date, not by version.`
        : `No P1 or P2 incidents opened between ${formatDate(incidents.from)} and ${formatDate(incidents.to)}.`}
    </Typography>
    {incidents.list.length > 0 && (
      <Table size="small">
        <TableHead>
          <TableRow sx={HEAD_SX}>
            <TableCell>Incident</TableCell>
            <TableCell>Description</TableCell>
            <TableCell>Priority</TableCell>
            <TableCell>State</TableCell>
            <TableCell>Opened</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {incidents.list.map((i) => (
            <TableRow key={i.number} hover>
              <TableCell sx={{ fontWeight: 600 }}>{i.number}</TableCell>
              <TableCell>{i.title}</TableCell>
              <TableCell>{i.priority}</TableCell>
              <TableCell>{i.state}</TableCell>
              <TableCell>{formatDate(i.openedAt)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    )}
  </Paper>
);

const Microservices = () => {
  const { data, loading, error } = useMicroservices();
  return (
    <Paper variant="outlined" sx={{ ...CARD_SX, overflowX: 'auto' }}>
      <Typography sx={{ fontWeight: 600, color: DEEP_GREEN, p: 2, pb: 0.5 }}>Microservices: newest version against the previous one</Typography>
      <Typography variant="body2" sx={{ color: MUTED, px: 2, pb: 1 }}>
        Microservice logs carry their version, so the error share can be compared exactly. Last {data ? data.lookbackDays : 14} days, services with enough logs on both versions. A version with little traffic can look worse than it is.
      </Typography>
      {loading && !data && <Box sx={{ p: 2 }}><CircularProgress size={20} /></Box>}
      {error && <Alert severity="warning" sx={{ m: 2, mt: 0 }}>{error}</Alert>}
      {data && data.stale && data.asOf && <SyncedLine asOf={data.asOf} stale />}
      {data && data.rows.length === 0 && <Typography variant="body2" sx={{ color: MUTED, p: 2, pt: 0 }}>No microservice has logs on two versions in this period.</Typography>}
      {data && data.rows.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow sx={HEAD_SX}>
              <TableCell>Service</TableCell>
              <TableCell>Version</TableCell>
              <TableCell align="right">Error share</TableCell>
              <TableCell>Previous version</TableCell>
              <TableCell align="right">Error share</TableCell>
              <TableCell align="right">Change</TableCell>
              <TableCell>Result</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.rows.map((r) => (
              <TableRow key={r.service} hover>
                <TableCell sx={{ fontWeight: 600 }}>{r.service}</TableCell>
                <TableCell>{r.current.version}</TableCell>
                <TableCell align="right" sx={NUMERALS}>{formatShare(r.current.share)}</TableCell>
                <TableCell>{r.previous.version}</TableCell>
                <TableCell align="right" sx={NUMERALS}>{formatShare(r.previous.share)}</TableCell>
                <TableCell align="right" sx={{ ...NUMERALS, fontWeight: 700, color: VERDICT_COLOR[r.status] }}>{formatRatio(r.ratio)}</TableCell>
                <TableCell><VerdictChip verdict={r.status} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Paper>
  );
};

const HealthView = ({ overview, onOpenPlans }) => {
  const { entries, entry, select, loading: catalogLoading, rolloutVersion } = useVersionPicker(overview, { skipSmall: true });
  const { data, loading, error, refreshError, reload } = useHealth(rolloutVersion);
  const [starting, setStarting] = useState(false);
  const [syncError, setSyncError] = useState('');
  const shown = rolloutVersion ? data : null;

  const syncing = !!(data && data.sync && data.sync.syncing);
  useEffect(() => {
    if (!syncing) return undefined;
    const timer = setInterval(reload, POLL_MS);
    return () => clearInterval(timer);
  }, [syncing, reload]);

  const handleSync = async () => {
    setStarting(true);
    setSyncError('');
    try {
      await startHealthSync();
      await reload();
    } catch (err) {
      setSyncError(err.message);
    } finally {
      setStarting(false);
    }
  };

  if (!entries.length) return <Typography sx={{ color: MUTED }}>No deployment runs are recorded yet.</Typography>;

  return (
    <Box sx={{ display: 'grid', gap: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <VersionPicker entries={entries} selected={entry ? entry.version : ''} onChange={select} loading={loading || catalogLoading} />
        <SyncLine sync={shown ? shown.sync : null} onSync={handleSync} starting={starting} error={syncError} />
      </Box>
      {entry && !entry.hasRollout && <NoRolloutNotice entry={entry} historyStart={overview.history.earliest} onOpenPlans={onOpenPlans} />}
      {error && <Alert severity="error">{error}</Alert>}
      {refreshError && <Alert severity="warning">Could not refresh: {refreshError}. Showing the last data loaded.</Alert>}
      {shown && shown.verdict === 'insufficient' && <Alert severity="info">{shown.reason}</Alert>}
      {shown && shown.verdict !== 'insufficient' && (
        <>
          {shown.reason && <Alert severity="warning">{shown.reason}</Alert>}
          <Summary data={shown} />
          {shown.waves.length > 0 && <WaveErrors waves={shown.waves} />}
          {shown.bugs && <BugsCard bugs={shown.bugs} asOf={shown.asOf && shown.asOf.bugs} stale={shown.staleSources.includes('bugs')} />}
          {shown.incidents && <IncidentsCard incidents={shown.incidents} asOf={shown.asOf && shown.asOf.incidents} stale={shown.staleSources.includes('incidents')} />}
          <ScoreBreakdown score={shown.score} />
        </>
      )}
      <Microservices />
    </Box>
  );
};

export default HealthView;
